import { Toaster } from "@/components/ui/sonner";
import { Analytics } from "@vercel/analytics/react";
import { lazy, startTransition, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import pkg from "../package.json";
import { PerformanceStatsOverlay } from "./components/PerformanceStatsOverlay";
import { SettingsPanel } from "./components/Settings";
import { Badge } from "./components/ui/badge";
import { ButtonGroup } from "./components/ui/button-group";
import { Card, CardContent } from "./components/ui/card";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupText, InputGroupTextarea } from "./components/ui/input-group";
import { Spinner } from "./components/ui/spinner";
import { useAI } from "./hooks/useAI";
import { useAutoHideUI } from "./hooks/useAutoHideUI";
import { DEFAULT_BLISTER_SETTINGS, normalizeBlisterSettings } from "./lib/blister-settings";
import { DEFAULT_FRACTAL_SETTINGS, normalizeFractalSettings } from "./lib/fractal-settings";
import { DEFAULT_KALEIDOSCOPE_SETTINGS, normalizeKaleidoscopeSettings } from "./lib/kaleidoscope-settings";
import {
  applyBlisterPerformanceTier,
  applyFractalPerformanceTier,
  applyKaleidoscopePerformanceTier,
  type PerformanceTier,
} from "./lib/performance-profile";
import {
  buildSettingsHash,
  mergeSharedSettings,
  parseSettingsHash,
  type SharedAppState,
  type ViewMode,
} from "./lib/shareable-settings";
import type { PerformanceSnapshot } from "./types/performance";

declare const __APP_VERSION__: string;

const Glitter = lazy(() => import("./components/Glitter").then((module) => ({ default: module.Glitter })));
const Fractal = lazy(() => import("./components/Fractal").then((module) => ({ default: module.Fractal })));
const Kaleidoscope = lazy(() => import("./components/Kaleidoscope").then((module) => ({ default: module.Kaleidoscope })));

function loadStoredBlisterSettings() {
  try {
    const saved = localStorage.getItem("blister-settings");
    if (saved) {
      return normalizeBlisterSettings(JSON.parse(saved));
    }
  } catch {
  }

  return { ...DEFAULT_BLISTER_SETTINGS };
}

function loadStoredKaleidoscopeSettings() {
  try {
    const saved = localStorage.getItem("kaleidoscope-settings");
    if (saved) {
      return normalizeKaleidoscopeSettings(JSON.parse(saved));
    }
  } catch {
  }

  return { ...DEFAULT_KALEIDOSCOPE_SETTINGS };
}

function loadStoredFractalSettings() {
  try {
    const saved = localStorage.getItem("fractal-settings");
    if (saved) {
      return normalizeFractalSettings(JSON.parse(saved));
    }
  } catch {
  }

  return normalizeFractalSettings(DEFAULT_FRACTAL_SETTINGS);
}

function getInitialSharedState(): SharedAppState {
  const baseState: SharedAppState = {
    viewMode: "glitter",
    blisterSettings: loadStoredBlisterSettings(),
    kaleidoscopeSettings: loadStoredKaleidoscopeSettings(),
    fractalSettings: loadStoredFractalSettings(),
  };
  const sharedState = parseSettingsHash(window.location.hash);

  if (sharedState) {
    return mergeSharedSettings(baseState, sharedState);
  }

  return baseState;
}

export default function App() {
  const initialStateRef = useRef<SharedAppState | null>(null);
  if (!initialStateRef.current) {
    initialStateRef.current = getInitialSharedState();
  }

  const [viewMode, setViewMode] = useState<ViewMode>(initialStateRef.current.viewMode);
  const [blisterSettings, setBlisterSettings] = useState(initialStateRef.current.blisterSettings);
  const [kaleidoscopeSettings, setKaleidoscopeSettings] = useState(initialStateRef.current.kaleidoscopeSettings);
  const [fractalSettings, setFractalSettings] = useState(initialStateRef.current.fractalSettings);
  const [performanceTier, setPerformanceTier] = useState<PerformanceTier>(() => {
    try {
      const stored = localStorage.getItem("blister-performance-tier");
      return stored === "battery" || stored === "balanced" || stored === "high" ? stored : "balanced";
    } catch {
      return "balanced";
    }
  });
  const [screenWakeLockEnabled, setScreenWakeLockEnabled] = useState(() => {
    try {
      return localStorage.getItem("blister-screen-wake-lock") === "true";
    } catch {
      return false;
    }
  });
  const [screenWakeLockActive, setScreenWakeLockActive] = useState(false);
  const [screenWakeLockSupported, setScreenWakeLockSupported] = useState(() =>
    typeof navigator !== "undefined" && "wakeLock" in navigator
  );

  const [speedBoosted, setSpeedBoosted] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [inputText, setInputText] = useState("");
  const [showPerformanceStats, setShowPerformanceStats] = useState(() => {
    try {
      return localStorage.getItem("blister-show-performance-stats") === "true";
    } catch {
      return false;
    }
  });
  const [performanceStats, setPerformanceStats] = useState<PerformanceSnapshot | null>(null);
  const [performanceSnapshotCopied, setPerformanceSnapshotCopied] = useState(false);
  const [shareHashCopied, setShareHashCopied] = useState(false);
  const debugBadgeClicksRef = useRef(0);
  const debugBadgeTimerRef = useRef<number | null>(null);
  const screenWakeLockRef = useRef<WakeLockSentinel | null>(null);
  const screenWakeLockReleaseHandlerRef = useRef<(() => void) | null>(null);

  const MAX_CHARACTERS = 120;
  const charactersLeft = MAX_CHARACTERS - inputText.length;
  const fractalPresetOnly = viewMode === "fractal";

  const { getBlisterAISettings, getKaleidoscopeAISettings } = useAI();

  useEffect(() => {
    const timer = setTimeout(() => setSpeedBoosted(false), 100);
    return () => clearTimeout(timer);
  }, []);

  // Save settings to localStorage
  useEffect(() => {
    localStorage.setItem("blister-settings", JSON.stringify(blisterSettings));
  }, [blisterSettings]);

  useEffect(() => {
    localStorage.setItem("kaleidoscope-settings", JSON.stringify(kaleidoscopeSettings));
  }, [kaleidoscopeSettings]);

  useEffect(() => {
    localStorage.setItem("fractal-settings", JSON.stringify(fractalSettings));
  }, [fractalSettings]);

  useEffect(() => {
    localStorage.setItem("blister-show-performance-stats", String(showPerformanceStats));
  }, [showPerformanceStats]);

  useEffect(() => {
    localStorage.setItem("blister-performance-tier", performanceTier);
  }, [performanceTier]);

  useEffect(() => {
    localStorage.setItem("blister-screen-wake-lock", String(screenWakeLockEnabled));
  }, [screenWakeLockEnabled]);

  useEffect(() => {
    let cancelled = false;

    const releaseWakeLock = async () => {
      const sentinel = screenWakeLockRef.current;

      if (!sentinel) {
        setScreenWakeLockActive(false);
        return;
      }

      if (screenWakeLockReleaseHandlerRef.current) {
        sentinel.removeEventListener("release", screenWakeLockReleaseHandlerRef.current);
        screenWakeLockReleaseHandlerRef.current = null;
      }

      screenWakeLockRef.current = null;
      setScreenWakeLockActive(false);

      if (!sentinel.released) {
        try {
          await sentinel.release();
        } catch {
        }
      }
    };

    const requestWakeLock = async () => {
      if (!screenWakeLockEnabled || document.visibilityState !== "visible") {
        await releaseWakeLock();
        return;
      }

      if (screenWakeLockRef.current) {
        return;
      }

      if (!("wakeLock" in navigator)) {
        setScreenWakeLockSupported(false);
        setScreenWakeLockActive(false);
        return;
      }

      setScreenWakeLockSupported(true);

      try {
        const sentinel = await navigator.wakeLock.request("screen");

        if (cancelled || !screenWakeLockEnabled) {
          await sentinel.release();
          return;
        }

        const handleRelease = () => {
          if (screenWakeLockRef.current === sentinel) {
            screenWakeLockRef.current = null;
          }
          screenWakeLockReleaseHandlerRef.current = null;
          setScreenWakeLockActive(false);
        };

        sentinel.addEventListener("release", handleRelease);
        screenWakeLockReleaseHandlerRef.current = handleRelease;
        screenWakeLockRef.current = sentinel;
        setScreenWakeLockActive(true);
      } catch {
        setScreenWakeLockActive(false);
      }
    };

    void requestWakeLock();

    const handleVisibilityChange = () => {
      void requestWakeLock();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      void releaseWakeLock();
    };
  }, [screenWakeLockEnabled]);

  useEffect(() => {
    if (!showPerformanceStats) {
      setPerformanceStats(null);
    }
  }, [showPerformanceStats, viewMode]);

  useEffect(() => {
    if (!showPerformanceStats || viewMode === "glitter") {
      return;
    }

    let animationFrame = 0;
    let previousTime = performance.now();
    let lastReportTime = previousTime;
    const frameDurations: number[] = [];

    const sampleFrame = (now: number) => {
      const frameDuration = Math.max(now - previousTime, 0.1);
      previousTime = now;
      frameDurations.push(frameDuration);

      while (frameDurations.length > 600) {
        frameDurations.shift();
      }

      if (now - lastReportTime >= 400) {
        const sampleWindowMs = Math.max(now - lastReportTime, 1);
        const avgFrameDuration = frameDurations.reduce((sum, value) => sum + value, 0) / frameDurations.length;
        const maxFrameDuration = Math.max(...frameDurations);
        const memory = (performance as Performance & { memory?: { usedJSHeapSize?: number } }).memory;

        setPerformanceStats({
          fps: 1000 / frameDuration,
          avgFps: 1000 / avgFrameDuration,
          minFps: 1000 / maxFrameDuration,
          frameTime: frameDuration,
          dpr: window.devicePixelRatio || 1,
          drawCalls: 0,
          triangles: 0,
          points: 0,
          geometries: 0,
          textures: 0,
          programs: 0,
          particleCount: 0,
          sampleWindowSeconds: sampleWindowMs / 1000,
          jsHeapMb: typeof memory?.usedJSHeapSize === "number" ? memory.usedJSHeapSize / (1024 * 1024) : undefined,
        });

        lastReportTime = now;
      }

      animationFrame = window.requestAnimationFrame(sampleFrame);
    };

    animationFrame = window.requestAnimationFrame(sampleFrame);

    return () => {
      window.cancelAnimationFrame(animationFrame);
    };
  }, [showPerformanceStats, viewMode]);

  useEffect(() => {
    if (!performanceSnapshotCopied) {
      return;
    }
    const timeout = window.setTimeout(() => setPerformanceSnapshotCopied(false), 1600);
    return () => window.clearTimeout(timeout);
  }, [performanceSnapshotCopied]);

  useEffect(() => {
    if (!shareHashCopied) {
      return;
    }
    const timeout = window.setTimeout(() => setShareHashCopied(false), 1600);
    return () => window.clearTimeout(timeout);
  }, [shareHashCopied]);

  useEffect(() => {
    return () => {
      if (debugBadgeTimerRef.current !== null) {
        window.clearTimeout(debugBadgeTimerRef.current);
      }
    };
  }, []);

  const handleCopyPerformanceSnapshot = async () => {
    if (!performanceStats) {
      return;
    }

    const payload = {
      capturedAt: new Date().toISOString(),
      version: pkg.version,
      viewMode,
      performance: performanceStats,
      blisterSettings: viewMode === "glitter" ? blisterSettings : null,
      fractalSettings: viewMode === "fractal" ? fractalSettings : null,
      kaleidoscopeSettings: viewMode === "kaleidoscope" ? kaleidoscopeSettings : null,
    };
    const text = JSON.stringify(payload, null, 2);

    try {
      await navigator.clipboard.writeText(text);
      setPerformanceSnapshotCopied(true);
      toast("Performance snapshot copied");
    } catch {
      toast("Clipboard copy failed");
    }
  };

  const handleCopyShareHash = async () => {
    const hash = buildSettingsHash({
      viewMode,
      blisterSettings,
      kaleidoscopeSettings,
      fractalSettings,
    });

    if (window.location.hash !== hash) {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${hash}`);
    }

    try {
      await navigator.clipboard.writeText(hash);
      setShareHashCopied(true);
      toast("Settings hash copied");
    } catch {
      toast("Clipboard copy failed");
    }
  };

  const handleApplyShareHash = (value: string) => {
    const parsedSettings = parseSettingsHash(value);

    if (!parsedSettings) {
      toast("Invalid settings hash");
      return false;
    }

    const sharedState = mergeSharedSettings(
      {
        viewMode,
        blisterSettings,
        kaleidoscopeSettings,
        fractalSettings,
      },
      parsedSettings
    );

    startTransition(() => {
      setViewMode(sharedState.viewMode);
      setBlisterSettings(sharedState.blisterSettings);
      setKaleidoscopeSettings(sharedState.kaleidoscopeSettings);
      setFractalSettings(sharedState.fractalSettings);
    });

    const hash = buildSettingsHash(sharedState);
    if (window.location.hash !== hash) {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${hash}`);
    }

    toast("Settings restored from shared hash");
    return true;
  };

  const handleDebugBadgeClick = () => {
    debugBadgeClicksRef.current += 1;

    if (debugBadgeTimerRef.current !== null) {
      window.clearTimeout(debugBadgeTimerRef.current);
    }

    if (debugBadgeClicksRef.current >= 5) {
      debugBadgeClicksRef.current = 0;
      debugBadgeTimerRef.current = null;
      const next = !showPerformanceStats;
      setShowPerformanceStats(next);
      toast(next ? "Debug stats enabled" : "Debug stats hidden");
      return;
    }

    debugBadgeTimerRef.current = window.setTimeout(() => {
      debugBadgeClicksRef.current = 0;
      debugBadgeTimerRef.current = null;
    }, 1200);
  };

  // Log version on mount
  useEffect(() => {
    // @ts-ignore
    console.log(`✨ Blisters version: ${pkg.version} ✨`);
  }, []);

  // Register service worker for PWA
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      window.addEventListener("load", () => {
        navigator.serviceWorker.register("/sw.js").catch(() => { });
      });
    }
  }, []);

  // Auto-update PWA if version changes
  useEffect(() => {
    const versionKey = "blister-app-version";
    const currentVersion = __APP_VERSION__;
    const storedVersion = localStorage.getItem(versionKey);

    if (storedVersion && storedVersion !== currentVersion) {
      localStorage.setItem(versionKey, currentVersion);
      if (navigator.serviceWorker?.controller) {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          registrations.forEach((reg) => reg.unregister());
          window.location.reload();
        });
      } else {
        window.location.reload();
      }
    } else {
      localStorage.setItem(versionKey, currentVersion);
    }
  }, []);

  useEffect(() => {
    const hash = buildSettingsHash({
      viewMode,
      blisterSettings,
      kaleidoscopeSettings,
      fractalSettings,
    });

    if (window.location.hash !== hash) {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${hash}`);
    }
  }, [viewMode, blisterSettings, kaleidoscopeSettings, fractalSettings]);

  useEffect(() => {
    const handleHashChange = () => {
      const parsedSettings = parseSettingsHash(window.location.hash);

      if (!parsedSettings) {
        return;
      }

      const sharedState = mergeSharedSettings(
        {
          viewMode,
          blisterSettings,
          kaleidoscopeSettings,
          fractalSettings,
        },
        parsedSettings
      );

      startTransition(() => {
        setViewMode(sharedState.viewMode);
        setBlisterSettings(sharedState.blisterSettings);
        setKaleidoscopeSettings(sharedState.kaleidoscopeSettings);
        setFractalSettings(sharedState.fractalSettings);
      });

      toast("Settings restored from URL");
    };

    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [viewMode, blisterSettings, kaleidoscopeSettings, fractalSettings]);

  // Use auto-hide for UI panels
  const rawUiVisible = useAutoHideUI(5000);
  const uiVisible = isEditing ? true : rawUiVisible;
  const renderedBlisterSettings = useMemo(
    () => {
      const adjustedSettings = applyBlisterPerformanceTier(blisterSettings, performanceTier);
      return {
        ...adjustedSettings,
        speed: speedBoosted ? 10 : adjustedSettings.speed,
      };
    },
    [blisterSettings, performanceTier, speedBoosted]
  );
  const renderedFractalSettings = useMemo(
    () => applyFractalPerformanceTier(fractalSettings, performanceTier),
    [fractalSettings, performanceTier]
  );
  const renderedKaleidoscopeSettings = useMemo(
    () => applyKaleidoscopePerformanceTier(kaleidoscopeSettings, performanceTier),
    [kaleidoscopeSettings, performanceTier]
  );

  return (
    <div className="w-full h-screen relative overflow-hidden">
      <Analytics />
      <Toaster />
      <Suspense fallback={<div className="absolute inset-0 bg-black" />}>
        {viewMode === "glitter" ? (
          <Glitter
            settings={renderedBlisterSettings}
            showPerformanceStats={showPerformanceStats}
            onPerformanceStats={setPerformanceStats}
            performanceTier={performanceTier}
          />
        ) : viewMode === "fractal" ? (
          <Fractal {...renderedFractalSettings} performanceTier={performanceTier} />
        ) : (
          <Kaleidoscope {...renderedKaleidoscopeSettings} performanceTier={performanceTier} />
        )}
      </Suspense>
      <PerformanceStatsOverlay
        stats={performanceStats}
        visible={showPerformanceStats}
        onCopySnapshot={handleCopyPerformanceSnapshot}
        copied={performanceSnapshotCopied}
      />
      {/* UI Panels */}
      <SettingsPanel
        viewMode={viewMode}
        blisterSettings={blisterSettings}
        onBlisterChange={setBlisterSettings}
        fractalSettings={fractalSettings}
        onFractalChange={setFractalSettings}
        kaleidoscopeSettings={kaleidoscopeSettings}
        onKaleidoscopeChange={setKaleidoscopeSettings}
        onCopyShareHash={handleCopyShareHash}
        onApplyShareHash={handleApplyShareHash}
        shareHashCopied={shareHashCopied}
        performanceTier={performanceTier}
        onPerformanceTierChange={setPerformanceTier}
        screenWakeLockEnabled={screenWakeLockEnabled}
        onScreenWakeLockChange={setScreenWakeLockEnabled}
        screenWakeLockSupported={screenWakeLockSupported}
        screenWakeLockActive={screenWakeLockActive}
        uiVisible={uiVisible}
      />
      {/* Combined Input and Mode Switching Panel */}
      <Card
        className={`z-40 fixed left-1/2 bottom-[calc(env(safe-area-inset-bottom)+0.75rem)] md:bottom-8 -translate-x-1/2 w-[min(400px,calc(100vw-24px))] transition-opacity duration-500 p-0 ${uiVisible ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
      >
        <CardContent className="p-0">
          {/* AI Input Form */}
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const text = inputText.trim();
              if (!text) return;
              if (fractalPresetOnly) {
                setInputText("");
                setIsEditing(false);
                toast("Choose a fractal preset");
                return;
              }
              setLoading(true);
              setIsEditing(false);
              try {
                if (viewMode === "glitter") {
                  const config = await getBlisterAISettings(text);
                  if (config) {
                    setBlisterSettings((prev: typeof blisterSettings) =>
                      normalizeBlisterSettings({
                        ...prev,
                        ...config,
                      })
                    );
                  } else {
                    console.error("Invalid settings from AI or JSON parse error");
                  }
                } else {
                  const config = await getKaleidoscopeAISettings(text);
                  if (config) {
                    setKaleidoscopeSettings((prev) =>
                      normalizeKaleidoscopeSettings({
                        ...prev,
                        ...config,
                      })
                    );
                  } else {
                    console.error("Invalid settings from AI or JSON parse error");
                  }
                }
              } catch (err: any) {
                console.error("Invalid settings: " + (err?.message || err));
              } finally {
                setLoading(false);
              }
              setInputText("");
            }}
          >
            <InputGroup data-disabled={loading || fractalPresetOnly} className="max-md:rounded-lg max-md:bg-black/28">
              {/* Header with Mode Switching */}
              <InputGroupAddon align="block-start" className="max-md:px-3 max-md:pt-2 max-md:pb-1">
                <InputGroupText className="font-medium max-md:text-xs">
                  {fractalPresetOnly ? "Mode" : "AI Mood Settings"}
                </InputGroupText>
                <div className="ml-auto">
                  <ButtonGroup>
                    <InputGroupButton
                      type="button"
                      variant={viewMode === "glitter" ? "default" : "ghost"}
                      size="xs"
                      onClick={() => setViewMode("glitter")}
                    >
                      ✨
                    </InputGroupButton>
                    <InputGroupButton
                      type="button"
                      variant={viewMode === "fractal" ? "default" : "ghost"}
                      size="xs"
                      onClick={() => setViewMode("fractal")}
                    >
                      🧬
                    </InputGroupButton>
                    <InputGroupButton
                      type="button"
                      variant={viewMode === "kaleidoscope" ? "default" : "ghost"}
                      size="xs"
                      onClick={() => setViewMode("kaleidoscope")}
                    >
                      🔮
                    </InputGroupButton>
                  </ButtonGroup>
                </div>
              </InputGroupAddon>

              {!fractalPresetOnly ? (
              <>
              {/* Textarea Input */}
              <InputGroupTextarea
                id="ai-settings-input"
                name="ai-settings-input"
                placeholder={loading ? "Processing..." : "Describe the mood and feeling you want..."}
                className="min-h-[48px] py-2 text-sm resize-none md:min-h-[80px] md:py-3"
                value={inputText}
                maxLength={MAX_CHARACTERS}
                disabled={loading}
                onFocus={() => setIsEditing(true)}
                onBlur={(e) => {
                  if (!e.target.value) setIsEditing(false);
                }}
                onChange={(e) => {
                  setInputText(e.target.value);
                  setIsEditing(
                    !!e.target.value || document.activeElement === e.target
                  );
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    const form = e.currentTarget.closest("form");
                    if (form) {
                      form.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
                    }
                  }
                }}
              />

              {/* Footer with Character Count and Submit */}
              <InputGroupAddon align="block-end" className="max-md:px-3 max-md:pt-1 max-md:pb-2">
                <InputGroupText className="hidden text-muted-foreground text-xs md:flex">
                  {charactersLeft} characters left
                </InputGroupText>
                {loading ? (
                  <div className="ml-auto flex items-center gap-2">
                    <Spinner className="size-4" />
                    <InputGroupText className="text-xs">Processing...</InputGroupText>
                  </div>
                ) : (
                  <InputGroupButton
                    type="submit"
                    size="sm"
                    className="ml-auto"
                    disabled={fractalPresetOnly || !inputText.trim() || loading}
                  >
                    Apply ⏎
                  </InputGroupButton>
                )}
              </InputGroupAddon>
              </>
              ) : null}
            </InputGroup>
          </form>
        </CardContent>
      </Card>
      {/* Author signature in the bottom right */}
      <div
        className={`fixed right-3 bottom-3 text-muted-foreground text-[15px] z-10 transition-opacity duration-500`}
        style={{
          opacity: uiVisible ? 0.5 : 0,
        }}
      >
        <button
          type="button"
          className="cursor-pointer rounded-md"
          onClick={handleDebugBadgeClick}
          title="Author badge"
        >
          <Badge variant="outline">h3lpkey with 💖 v{pkg.version}</Badge>
        </button>
      </div>
    </div>
  );
}
